import { ErrorDeApi, esConflicto } from './error';
import type { ErrorApi } from './tipos';

// Se reexportan para que las pantallas tengan una sola puerta: `../api/cliente`.
export { ErrorDeApi, esConflicto };

const BASE = '/api';

/**
 * LOS TOKENS
 * ==========
 *
 * El `access` vive en memoria: un XSS lee localStorage, no una variable de módulo.
 *
 * El `refresh` viaja en el cuerpo (así lo decidió la API: `POST /auth/refrescar {refresh}`) y se
 * guarda en `sessionStorage`, que es de la pestaña y se borra al cerrarla. El mostrador está
 * abierto todo el día en una máquina compartida y un F5 accidental no puede dejar al cajero
 * afuera con gente esperando; pero tampoco puede quedar la sesión colgada para el siguiente que
 * se siente en esa máquina. Además rota en cada uso: el que se guardó ya no sirve dos veces.
 */
const CLAVE_REFRESH = 'planilla:refresh';
let token: string | null = null;
let renovando: Promise<string | null> | null = null;
let alPerderSesion: (() => void) | null = null;

function leerRefresh(): string | null {
  try {
    return sessionStorage.getItem(CLAVE_REFRESH);
  } catch {
    return null;
  }
}

function guardarRefresh(valor: string | null): void {
  try {
    if (valor) sessionStorage.setItem(CLAVE_REFRESH, valor);
    else sessionStorage.removeItem(CLAVE_REFRESH);
  } catch {
    /* sin almacenamiento: la sesión vale hasta que se recargue la página */
  }
}

/** Guarda el par de tokens. `guardarSesion(null)` cierra todo. */
export function guardarSesion(access: string | null, refresh?: string | null): void {
  token = access;
  if (refresh !== undefined) guardarRefresh(refresh);
  else if (access === null) guardarRefresh(null);
}

export function avisarSesionPerdida(fn: () => void): void {
  alPerderSesion = fn;
}

// --- Modo demostración -------------------------------------------------------------
//
// Si la API todavía no está levantada, el front no se queda en blanco: contesta con datos de
// ejemplo y lo dice en una franja, para que nadie confunda una demostración con la operación real.
// Se activa SOLO cuando el pedido no llega a destino (fetch rechaza) o con ?demo=1; un 4xx o un
// 5xx de la API es una respuesta y se muestra como tal.

let demostracion = false;

try {
  demostracion = new URLSearchParams(window.location.search).has('demo');
} catch {
  /* sin URL: el modo se resuelve con el primer pedido */
}

export function enDemostracion(): boolean {
  return demostracion;
}

function activarDemostracion(): void {
  demostracion = true;
}

/**
 * Los datos de ejemplo se cargan aparte y solo si hacen falta: son una red por si la API no
 * contesta, no parte del producto, y no tienen por qué pesar en el bundle que baja el mostrador
 * todas las mañanas.
 */
async function ejemplo<T>(ruta: string, metodo: string, cuerpo?: unknown): Promise<T> {
  const { respuestaDeEjemplo } = await import('./ejemplos');
  return respuestaDeEjemplo<T>(ruta, metodo, cuerpo);
}

export function renovarSesion(): Promise<string | null> {
  // Si llegan cinco pedidos con el token vencido, se renueva una sola vez.
  renovando ??= (async () => {
    try {
      const refresh = leerRefresh();
      if (demostracion) {
        const datos = await ejemplo<{ access: string; refresh: string }>('/auth/refrescar', 'POST');
        token = datos.access;
        return token;
      }
      if (!refresh) return null;
      const respuesta = await fetch(`${BASE}/auth/refrescar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      });
      if (!respuesta.ok) {
        // El refresh no sirve más (venció o se revocó): no se insiste.
        guardarRefresh(null);
        return null;
      }
      const datos = (await respuesta.json()) as { access: string; refresh: string };
      token = datos.access;
      guardarRefresh(datos.refresh);
      return token;
    } catch {
      // La API no contesta: se sigue con datos de ejemplo.
      activarDemostracion();
      const datos = await ejemplo<{ access: string; refresh: string }>('/auth/refrescar', 'POST');
      token = datos.access;
      return token;
    } finally {
      renovando = null;
    }
  })();
  return renovando;
}

export type Metodo = 'GET' | 'POST' | 'PATCH' | 'PUT';

interface Opciones {
  metodo?: Metodo;
  cuerpo?: unknown;
  reintentar?: boolean;
}

/**
 * Una sola puerta al backend: pone el token, renueva cuando vence, normaliza el error y devuelve
 * el dato. Las respuestas vienen sin envoltorio (la API devuelve el objeto y nada más), y los
 * montos vienen como cadena: acá no se convierte ni se redondea nada.
 */
export async function pedir<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const { metodo = 'GET', cuerpo, reintentar = true } = opciones;
  if (demostracion) return ejemplo<T>(ruta, metodo, cuerpo);

  let respuesta: Response;
  try {
    respuesta = await fetch(`${BASE}${ruta}`, {
      method: metodo,
      headers: {
        ...(cuerpo !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: cuerpo !== undefined ? JSON.stringify(cuerpo) : undefined,
    });
  } catch {
    activarDemostracion();
    return ejemplo<T>(ruta, metodo, cuerpo);
  }

  if (respuesta.status === 401 && reintentar && !ruta.startsWith('/auth/')) {
    const nuevo = await renovarSesion();
    if (nuevo) return pedir<T>(ruta, { ...opciones, reintentar: false });
    alPerderSesion?.();
  }

  if (respuesta.status === 204) return undefined as T;
  const datos = await respuesta.json().catch(() => null);
  if (!respuesta.ok) {
    const error = (datos ?? {}) as Partial<ErrorApi>;
    throw new ErrorDeApi(
      error.mensaje ?? 'No se pudo completar la operación. Probá de nuevo.',
      error.codigo ?? 'ERROR',
      respuesta.status,
      error.detalles,
    );
  }
  return datos as T;
}

/** Construye una query string ignorando lo vacío. */
export function consulta(parametros: Record<string, string | number | boolean | undefined | null>): string {
  const pares = Object.entries(parametros).filter(([, valor]) => valor !== undefined && valor !== null && valor !== '');
  if (pares.length === 0) return '';
  return `?${new URLSearchParams(pares.map(([clave, valor]) => [clave, String(valor)])).toString()}`;
}

/**
 * El 409 de "elegí en qué entidad entrar" no es un error: es una pregunta. La API la contesta así
 * porque recién al validar la clave sabe en cuántas entidades trabaja la persona.
 */
export function esElegirEntidad(error: unknown): error is ErrorDeApi & { detalles: { entidades: Array<{ id: string; nombre: string; rol: string }> } } {
  return error instanceof ErrorDeApi && error.codigo === 'ELEGIR_ENTIDAD';
}
